import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Users,
  Edit3,
  Globe,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Car,
  Shield,
  Cloud,
  Mail,
  MapPin,
  Euro,
  Sparkles,
  Package,
  Palette,
  Image as ImageIcon,
  Target,
  Zap
} from 'lucide-react';
import { onboardingAnalysisService, WebsiteAnalysisResult, DeepResearchResult } from '../../services/onboardingAnalysisService';
import { geminiService } from '../../services/geminiService';
import { moduleService } from '../../services/moduleService';
import { getModulesForIndustry, calculateTotalPrice, getRecommendationReason } from '../../data/moduleCatalog';
import { getTemplateById } from '../../data/systemOnboardingTemplates';
import { onboardingTemplateService } from '../../services/onboardingTemplateService';
import { organizationService } from '../../services/organizationService';
import { usersService } from '../../services/usersService';
import Echobot from '../Echobot/Echobot';
import { echobotService } from '../../services/echobotService';
import { imageGenerationService, GeneratedImage } from '../../services/imageGenerationService';
import { EchobotAnimation } from '../../types/echobot';

interface SmartOnboardingWizardProps {
  user: {
    uid: string;
    email: string | null;
    displayName: string | null;
  };
  onComplete: () => void;
}

interface WizardState {
  currentStep: number;  // 1-6
  entryMethod: 'website' | 'join' | 'manual';
  
  // Website Analysis
  websiteUrl: string;
  analysisResult?: WebsiteAnalysisResult;
  isAnalyzing: boolean;
  analysisError?: string;
  
  // Organization Data
  orgName: string;
  industry: 'automotive' | 'insurance' | 'saas' | 'consulting' | 'ecommerce' | 'agency' | 'finance';
  industryConfidence: number;
  location?: { city: string; country: string };
  
  // Image Selection
  generatedImages: Array<{ url: string; style: string; prompt: string }>;
  selectedImageIndex: number;
  // Store all scraped brand images
  brandImages: string[];
  // NEW: Track indices of user-selected scraped images
  selectedScrapedImageIndices: number[];
  
  // NEW: Track indices of user-selected colors
  selectedColorIndices: number[];
  
  // NEW: Track selection of strategic items
  selectedOfferings: string[];
  selectedKeywords: string[];
  
  // NEW: Track selected logo candidate
  selectedLogoIndex: number;
  
  // NEW: Editable image generation prompt
  imagePrompt: string;
  
  isGeneratingImages: boolean;
  
  // Video & Background Processing
  showIntroVideo: boolean;
  videoProgress: number; // 0-100
  backgroundProcessing: {
    scraping: 'pending' | 'running' | 'complete' | 'error';
    logoExtraction: 'pending' | 'running' | 'complete' | 'error';
    colorExtraction: 'pending' | 'running' | 'complete' | 'error';
    deepResearch: 'pending' | 'running' | 'complete' | 'error';
  };
  backgroundError?: string;
  
  // Deep Research Results
  isResearching: boolean;
  deepResearch?: {
    marketAnalysis: string;
    competitorInsights: Array<{
      name: string;
      website?: string;
      strengths: string[];
      weaknesses: string[];
      positioning: string;
    }>;
    brandPositioning: string;
    strategicRecommendations: string[];
    confidence: 'high' | 'medium' | 'low';
  };
  
  // Sales Strategy (NEW for Step 2.9)
  salesStrategy?: {
    businessModel: 'b2b' | 'b2c' | 'b2b2c';
    targetAudienceSegment: 'startup' | 'smb' | 'mid_market' | 'enterprise' | 'consumer' | 'mixed';
    targetAudienceDescription: string;
    products: Array<{
      name: string;
      description: string;
      pricing?: string;
      pricingModel?: 'subscription' | 'one_time' | 'usage_based' | 'quote';
      url?: string;
      selected?: boolean; // User selection
    }>;
    conversionPoints: Array<{
      type: 'form' | 'button' | 'cta' | 'phone' | 'email' | 'chat';
      label: string;
      url?: string;
    }>;
    idealCustomerProfile: {
      companySize?: string;
      budget?: string;
      industry?: string[];
      painPoints?: string[];
    };
    selectedCompetitors: string[]; // User can select from deepResearch competitors
  };

  // Module Selection
  selectedModules: string[];
  totalPrice: number;
  
  // Template & Team
  templateId: string;
  includeSampleData: boolean;
  teamInvites: string;  // Comma-separated emails
  
  // Creation
  isCreating: boolean;
  creationProgress: string[];
  
  // Reuse existing scraping
  existingScrapings: Array<{
    id: string;
    name: string;
    website: string;
    scrapedAt: Date;
    onboardingData: any;
  }>;
  showScrapingSelection: boolean;
}

export const SmartOnboardingWizard: React.FC<SmartOnboardingWizardProps> = ({ user, onComplete }) => {
  const { t } = useTranslation('onboarding');
  const [state, setState] = useState<WizardState>({
    currentStep: 1,
    entryMethod: 'website',
    websiteUrl: '',
    isAnalyzing: false,
    orgName: '',
    industry: 'consulting',
    industryConfidence: 0,
    generatedImages: [],
    selectedImageIndex: -1,
    brandImages: [],
    selectedScrapedImageIndices: [], // Init empty
    selectedColorIndices: [], // Init empty
    selectedOfferings: [], // Init empty
    selectedKeywords: [], // Init empty
    selectedLogoIndex: 0, // Default to first/best candidate
    imagePrompt: '', // Will be built dynamically
    isGeneratingImages: false,
    showIntroVideo: false,
    videoProgress: 0,
    backgroundProcessing: {
      scraping: 'pending',
      logoExtraction: 'pending',
      colorExtraction: 'pending',
      deepResearch: 'pending'
    },
    isResearching: false,
    selectedModules: ['crm_core'],  // Always include CRM Core
    totalPrice: 19,
    templateId: '',
    includeSampleData: true,
    teamInvites: '',
    isCreating: false,
    creationProgress: [],
    existingScrapings: [],
    showScrapingSelection: false
  });

  // Load existing scrapings on mount
  useEffect(() => {
    const loadExistingScrapings = async () => {
      try {
        const userOrgs = await organizationService.getUserOrganizations(user.uid);
        const scrapings = userOrgs
          .filter(org => org.onboardingData && org.onboardingData.scrapedAt)
          .map(org => ({
            id: org.id,
            name: org.name,
            website: org.onboardingData!.websiteUrl || org.website || '',
            scrapedAt: org.onboardingData!.scrapedAt instanceof Date 
              ? org.onboardingData!.scrapedAt 
              : (org.onboardingData!.scrapedAt as any)?.toDate?.() || new Date(),
            onboardingData: org.onboardingData
          }))
          .sort((a, b) => b.scrapedAt.getTime() - a.scrapedAt.getTime());
          
        setState(s => ({ ...s, existingScrapings: scrapings }));
      } catch (error) {
        console.error('Error loading existing scrapings:', error);
      }
    };
    
    loadExistingScrapings();
  }, [user.uid]);

  // Echobot state
  const [echobotMessage, setEchobotMessage] = useState('');
  const [echobotAnimation, setEchobotAnimation] = useState<EchobotAnimation>('wave');

  // Update Echobot message based on step changes
  useEffect(() => {
    const updateEchobotForStep = () => {
      switch (state.currentStep) {
        case 1:
          setEchobotMessage('Hallo! Ich bin Echobot und helfe dir beim Setup! 🤖');
          setEchobotAnimation('wave');
          break;
        case 2:
        case 2.1:
          // Video/Background processing step
          if (state.showIntroVideo) {
            setEchobotMessage('Das Video läuft - ich analysiere im Hintergrund! 🎬');
            setEchobotAnimation('question');
          } else if (state.analysisResult) {
            setEchobotMessage(`Perfekt! Ich habe ${state.analysisResult.business.industry} erkannt! ✨`);
            setEchobotAnimation('celebrate');
          } else if (state.backgroundError) {
            setEchobotMessage('Hmm, da gab es ein Problem... 😕');
            setEchobotAnimation('shake');
          } else {
            setEchobotMessage('Gib deine Website ein, ich analysiere sie automatisch! 🔍');
            setEchobotAnimation('question');
          }
          break;
        case 2.8: // Stylebook Step
          if (state.isGeneratingImages) {
            setEchobotMessage('Ich erstelle gerade Bilder für deine Marke... 🎨');
            setEchobotAnimation('question');
          } else {
            setEchobotMessage('Wähle deine Marken-Assets! 🎨');
            setEchobotAnimation('wave');
          }
          break;
        case 2.9: // Sales Strategy
          setEchobotMessage('Lass uns deine Sales-Strategie definieren! 🎯');
          setEchobotAnimation('wave');
          break;
        case 3:
          setEchobotMessage(`Ich schlage ${state.industry} vor, passt das? 🤔`);
          setEchobotAnimation('question');
          break;
        case 4:
          setEchobotMessage('Fast geschafft! Noch ein paar Details... 📝');
          setEchobotAnimation('wave');
          break;
        case 5:
          setEchobotMessage('Diese Module passen zu deiner Branche! 🎁');
          setEchobotAnimation('question');
          break;
        case 6:
          if (state.isCreating) {
            setEchobotMessage('Ich richte alles für dich ein... ⚙️');
            setEchobotAnimation('question');
          } else {
            setEchobotMessage('Geschafft! Willkommen bei Aideline! 🎉');
            setEchobotAnimation('celebrate');
          }
          break;
        default:
          setEchobotMessage('Ich bin hier um zu helfen! 🤖');
          setEchobotAnimation('wave');
      }
    };

    updateEchobotForStep();
  }, [state.currentStep, state.isAnalyzing, state.analysisResult, state.analysisError, state.industry, state.isCreating]);

  // ============================================================================
  // Step 1: Welcome & Entry Choice
  // ============================================================================

  const renderStep1 = () => (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-card rounded-2xl shadow-heavy max-w-2xl w-full p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-txt mb-2">🎉 Willkommen bei Aideline!</h1>
          <p className="text-lg text-txt-secondary">Wir richten Ihr CRM automatisch ein</p>
        </div>

        <div className="space-y-4 mb-6">
          {/* Website Analysis */}
          <button
            onClick={() => {
              if (state.existingScrapings.length > 0) {
                setState(s => ({ ...s, showScrapingSelection: true }));
              } else {
                setState(s => ({ ...s, entryMethod: 'website', currentStep: 2 }));
              }
            }}
            className="w-full p-6 border-2 border-accent bg-info-soft rounded-xl hover:bg-info-soft transition-all text-left group"
          >
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-info-soft rounded-lg flex items-center justify-center group-hover:bg-info-soft transition">
                <Globe className="w-6 h-6 text-accent" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-txt mb-1">Website analysieren</h3>
                <p className="text-sm text-txt-secondary mb-2">Empfohlen - Automatische Einrichtung</p>
                {state.existingScrapings.length > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-info-soft text-info mb-1">
                    {state.existingScrapings.length} Vorhandene gefunden
                  </span>
                )}
                <p className="text-sm text-txt-tertiary">⏱️ Dauer: ~2 Minuten</p>
              </div>
              <ArrowRight className="w-5 h-5 text-accent group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          {/* Join Team */}
          <button
            onClick={() => setState(s => ({ ...s, entryMethod: 'join', currentStep: 2 }))}
            className="w-full p-6 border-2 border-default rounded-xl hover:border-strong hover:bg-bg-surface transition-all text-left group"
          >
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-bg-surface rounded-lg flex items-center justify-center group-hover:bg-bg-surface-hover transition">
                <Users className="w-6 h-6 text-txt-secondary" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-txt mb-1">Team beitreten</h3>
                <p className="text-sm text-txt-secondary">Haben Sie einen Einladungscode?</p>
              </div>
              <ArrowRight className="w-5 h-5 text-txt-muted group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          {/* Manual Setup */}
          <button
            onClick={() => setState(s => ({ ...s, entryMethod: 'manual', currentStep: 2 }))}
            className="w-full p-6 border-2 border-default rounded-xl hover:border-strong hover:bg-bg-surface transition-all text-left group"
          >
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-bg-surface rounded-lg flex items-center justify-center group-hover:bg-bg-surface-hover transition">
                <Edit3 className="w-6 h-6 text-txt-secondary" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-txt mb-1">Manuell einrichten</h3>
                <p className="text-sm text-txt-secondary">Ich gebe die Details selbst ein</p>
              </div>
              <ArrowRight className="w-5 h-5 text-txt-muted group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // Step 2: Website Analysis
  // ============================================================================

  const handleAnalyzeWebsite = async () => {
    if (!state.websiteUrl.trim()) return;

    // Start video and background processing
    setState(s => ({
      ...s,
      showIntroVideo: true,
      currentStep: 2.1,
      analysisError: undefined,
      backgroundError: undefined,
      backgroundProcessing: {
        scraping: 'running',
        logoExtraction: 'pending',
        colorExtraction: 'pending',
        deepResearch: 'pending'
      }
    }));

    try {
      // Run comprehensive analysis with deep research
      const { analysis, deepResearch } = await onboardingAnalysisService.analyzeWebsiteWithDeepResearch(
        state.websiteUrl,
        (stage, status) => {
          setState(s => ({
            ...s,
            backgroundProcessing: {
              ...s.backgroundProcessing,
              [stage]: status
            }
          }));
        }
      );
      
      // When complete, set all results
      setState(s => ({
        ...s,
        analysisResult: analysis,
        deepResearch: deepResearch,
        orgName: analysis.business.name,
        industry: analysis.business.industry,
        industryConfidence: analysis.business.industryConfidence,
        location: analysis.business.location,
        templateId: analysis.business.industry,
        selectedModules: ['crm_core', ...analysis.analysis.recommendedModules],
        totalPrice: calculateTotalPrice(['crm_core', ...analysis.analysis.recommendedModules]),
        brandImages: analysis.analysis.brandImages || [],
        
        // Initialize selections with defaults
        selectedScrapedImageIndices: (analysis.analysis.brandImages || []).slice(0, 5).map((_, i) => i),
        selectedColorIndices: (analysis.analysis.brandColors || []).slice(0, 3).map((_, i) => i),
        selectedOfferings: analysis.analysis.detectedOfferings || [],
        selectedKeywords: (analysis.analysis.keywords || []).slice(0, 10),
        
        // Build default image prompt
        imagePrompt: buildDefaultImagePrompt({
          ...s,
          orgName: analysis.business.name,
          industry: analysis.business.industry,
          analysisResult: analysis
        }),
        
        backgroundProcessing: {
          scraping: 'complete',
          logoExtraction: 'complete',
          colorExtraction: 'complete',
          deepResearch: 'complete'
        }
        // currentStep stays 2.1 (video) - will change when video ends or user skips
      }));
      
    } catch (error) {
      setState(s => ({
        ...s,
        showIntroVideo: false,
        currentStep: 2,
        backgroundError: error instanceof Error ? error.message : 'Analyse fehlgeschlagen',
        backgroundProcessing: {
          scraping: 'error',
          logoExtraction: 'error',
          colorExtraction: 'error',
          deepResearch: 'error'
        }
      }));
    }
  };

  // Helper: Build default image generation prompt from scraped brand DNA
  const buildDefaultImagePrompt = (currentState: WizardState): string => {
    if (!currentState.analysisResult) return '';
    
    const { analysis } = currentState.analysisResult;
    
    return `Erstelle ein professionelles Markenbild für "${currentState.orgName}".

Industrie: ${currentState.industry}
Marken-Tonalität: ${analysis.brandTone || 'professionell'}
Farben: ${analysis.brandColors?.slice(0, 3).join(', ') || 'moderne Unternehmensfarben'}
Keywords: ${analysis.keywords?.slice(0, 5).join(', ') || ''}
USP: ${analysis.usp || ''}

Stil: Hochwertige Belichtung, professionelle Komposition, moderne Ästhetik.
Format: 16:9, 2K Auflösung, keine Texte oder Buchstaben im Bild.`;
  };

  const handleGenerateImages = async () => {
    if (!state.analysisResult) return;
    
    // Don't change step anymore, just toggle loading state
    setState(s => ({ ...s, isGeneratingImages: true }));
    
    try {
      // Get selected colors or fall back to all brand colors
      const selectedColors = state.selectedColorIndices.length > 0
        ? state.selectedColorIndices.map(i => state.analysisResult!.analysis.brandColors?.[i]).filter(Boolean) as string[]
        : state.analysisResult.analysis.brandColors || [];
      
      const images = await imageGenerationService.generateBrandImages({
        companyName: state.orgName,
        industry: state.industry,
        brandTone: state.analysisResult.analysis.brandTone || 'formal',
        keywords: state.analysisResult.analysis.keywords || [],
        colors: selectedColors // Pass brand colors for image generation
      });
      
      setState(s => ({
        ...s,
        generatedImages: images,
      isGeneratingImages: false,
      selectedImageIndex: images.length > 0 ? 0 : -1 // Auto-select first generated image
      }));
    } catch (error) {
      console.error('Failed to generate images:', error);
      setState(s => ({ ...s, isGeneratingImages: false }));
      // Don't skip to next step - stay on Stylebook page even if generation fails
    }
  };

  const renderStep2 = () => {
    if (state.entryMethod === 'join') {
      // Join team flow - simplified
      return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-card rounded-2xl shadow-heavy max-w-lg w-full p-8">
            <button
              onClick={() => setState(s => ({ ...s, currentStep: 1 }))}
              className="mb-4 text-txt-secondary hover:text-txt flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> {t('step2.back')}
            </button>
            <h2 className="text-2xl font-bold mb-4">{t('step2.joinTeam')}</h2>
            <p className="text-txt-secondary mb-6">{t('step2.enterInviteCode')}</p>
            <input
              type="text"
              placeholder={t('step2.inviteCodePlaceholder')}
              className="w-full px-4 py-3 border rounded-lg mb-4"
            />
            <button className="w-full bg-accent text-txt-inverted py-3 rounded-lg hover:bg-accent-bright">
              {t('step2.acceptInvitation')}
            </button>
          </div>
        </div>
      );
    }

    // OLD isAnalyzing loading screen REMOVED - now using video screen (step 2.1)

    if (state.currentStep === 2.5 && state.analysisResult) {
      // Results review
      return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-card rounded-2xl shadow-heavy max-w-lg w-full p-8">
            <button
              onClick={() => setState(s => ({ ...s, currentStep: 2, analysisResult: undefined }))}
              className="mb-4 text-txt-secondary hover:text-txt flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> {t('step2.back')}
            </button>
            
            <div className="text-center mb-6">
              <CheckCircle2 className="w-12 h-12 text-success mx-auto mb-2" />
              <h2 className="text-2xl font-bold">{t('analysis.foundCompany')}</h2>
              <p className="text-txt-secondary">{t('analysis.reviewDetails')}</p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-1">{t('analysis.companyName')}</label>
                <input
                  type="text"
                  value={state.orgName}
                  onChange={(e) => setState(s => ({ ...s, orgName: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('analysis.industry')}</label>
                <div className="flex items-center space-x-2">
                  <select
                    value={state.industry}
                    onChange={(e) => {
                      const newIndustry = e.target.value as any;
                      // Also update templateId and reset modules based on new industry
                      const { recommended } = getModulesForIndustry(newIndustry);
                      const recommendedIds = recommended.map(m => m.id);
                      const newSelectedModules = ['crm_core', ...recommendedIds];
                      
                      setState(s => ({ 
                        ...s, 
                        industry: newIndustry,
                        templateId: newIndustry,
                        selectedModules: newSelectedModules,
                        totalPrice: calculateTotalPrice(newSelectedModules)
                      }));
                    }}
                    className="flex-1 px-4 py-2 border rounded-lg"
                  >
                    <option value="automotive">🚗 {t('industries.automotive')}</option>
                    <option value="insurance">🛡️ {t('industries.insurance')}</option>
                    <option value="saas">☁️ {t('industries.saas')}</option>
                    <option value="consulting">💼 {t('industries.consulting')}</option>
                    <option value="ecommerce">🛒 {t('industries.ecommerce')}</option>
                    <option value="agency">🎨 {t('industries.agency')}</option>
                    <option value="finance">🏦 {t('industries.finance')}</option>
                  </select>
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    state.industryConfidence >= 80 ? 'bg-success-soft text-success' :
                    state.industryConfidence >= 60 ? 'bg-warning-soft text-warning' :
                    'bg-danger-soft text-danger'
                  }`}>
                    {state.industryConfidence}%
                  </span>
                </div>
                <p className="text-xs text-txt-tertiary mt-1">
                  🎯 {t('analysis.confidence')}: {state.industryConfidence >= 80 ? t('analysis.confidenceVeryHigh') : state.industryConfidence >= 60 ? t('analysis.confidenceHigh') : t('analysis.confidenceLow')}
                </p>
              </div>

              {state.location && (
                <div>
                  <label className="block text-sm font-medium mb-1">📍 {t('analysis.location')}</label>
                  <input
                    type="text"
                    value={`${state.location.city}, ${state.location.country}`}
                    readOnly
                    className="w-full px-4 py-2 border rounded-lg bg-bg-surface"
                  />
                </div>
              )}

              {/* NEW: Brand Tone Display */}
              {state.analysisResult?.analysis.brandTone && (
                <div>
                  <label className="block text-sm font-medium mb-1">💬 {t('analysis.brandTone')}</label>
                  <div className="flex items-center px-4 py-2 border rounded-lg bg-bg-surface text-txt-secondary">
                    {state.analysisResult.analysis.brandTone === 'formal' ? `👔 ${t('analysis.brandToneFormal')}` : `👋 ${t('analysis.brandToneCasual')}`}
                    <span className="ml-2 text-xs text-txt-tertiary">{t('analysis.forAiAssistant')}</span>
                  </div>
                </div>
              )}

              {/* NEW: Logo Preview */}
              {state.analysisResult?.analysis.logoUrl && (
                <div>
                  <label className="block text-sm font-medium mb-1">🖼️ {t('analysis.logoFound')}</label>
                  <div className="p-2 border rounded-lg bg-bg-card inline-block">
                    <img 
                      src={state.analysisResult.analysis.logoUrl} 
                      alt="Company Logo" 
                      className="h-12 object-contain" 
                    />
                  </div>
                </div>
              )}
            </div>

            <p className="text-sm text-txt-tertiary mb-4">💡 Stimmt etwas nicht? Einfach bearbeiten!</p>

            <button
              onClick={() => setState(s => ({ 
                ...s, 
                currentStep: 2.8,
                // Build default image prompt from scraped data
                imagePrompt: buildDefaultImagePrompt(s)
              }))}
              className="w-full bg-accent text-txt-inverted py-3 rounded-lg hover:bg-accent-bright flex items-center justify-center font-bold shadow-card shadow-glow-accent transition-all hover:scale-[1.02]"
            >
              <ArrowRight className="w-5 h-5 mr-2" />
              Weiter zum Stylebook
            </button>
            <button
              onClick={() => setState(s => ({ ...s, currentStep: 3 }))}
              className="w-full mt-3 text-txt-tertiary py-2 hover:text-txt-secondary flex items-center justify-center text-sm"
            >
              Überspringen <ArrowRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      );
    }

    // Step 2.8 is now the Stylebook Selection (below) - old separate image generation step removed

    // URL Input
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-bg-card rounded-2xl shadow-heavy max-w-lg w-full p-8">
          <button
            onClick={() => setState(s => ({ ...s, currentStep: 1 }))}
            className="mb-4 text-txt-secondary hover:text-txt flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
          </button>
          
          <div className="mb-6">
            <Globe className="w-12 h-12 text-accent mb-4" />
            <h2 className="text-2xl font-bold mb-2">Ihre Website analysieren</h2>
            <p className="text-txt-secondary">Wir analysieren Ihre Website, um:</p>
            <ul className="mt-2 space-y-1 text-sm text-txt-secondary">
              <li>✓ Ihre Branche zu erkennen</li>
              <li>✓ Firmeninformationen zu finden</li>
              <li>✓ Passende Module zu empfehlen</li>
            </ul>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Website-URL</label>
            <input
              type="url"
              value={state.websiteUrl}
              onChange={(e) => setState(s => ({ ...s, websiteUrl: e.target.value }))}
              placeholder="https://www.ihre-firma.de"
              className="w-full px-4 py-3 border rounded-lg"
            />
            <p className="text-xs text-txt-tertiary mt-1">💡 Tipp: Geben Sie Ihre Haupt-Website ein</p>
          </div>

          {(state.analysisError || state.backgroundError) && (
            <div className="mb-4 p-3 bg-danger-soft border border-danger rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-danger mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-danger">{state.backgroundError || state.analysisError}</p>
            </div>
          )}

          <button
            onClick={handleAnalyzeWebsite}
            disabled={!state.websiteUrl.trim()}
            className="w-full bg-accent text-txt-inverted py-3 rounded-lg hover:bg-accent-bright disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            Analysieren <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Step 2.9: Sales Strategy
  // ============================================================================

  const handleExtractSalesStrategy = async () => {
    if (!state.analysisResult) return;
    
    setState(s => ({ ...s, isAnalyzing: true }));
    
    try {
      const strategy = await geminiService.extractSalesStrategy({
        markdown: state.analysisResult.scraping.markdown,
        html: state.analysisResult.scraping.html,
        businessName: state.orgName,
        industry: state.industry,
        businessModel: state.analysisResult.analysis.businessModel,
        competitors: state.analysisResult.analysis.competitors
      });
      
      setState(s => ({
        ...s,
        salesStrategy: {
          ...strategy,
          selectedCompetitors: state.deepResearch?.competitorInsights.map(c => c.name) || [],
          products: strategy.products.map(p => ({ ...p, selected: true })) // All selected by default
        },
        isAnalyzing: false
      }));
    } catch (error) {
      console.error('Failed to extract sales strategy:', error);
      setState(s => ({ ...s, isAnalyzing: false }));
    }
  };

  const renderStep2_9 = () => {
    const hasStrategy = !!state.salesStrategy;
    
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-bg-card rounded-2xl shadow-heavy w-full max-w-5xl max-h-[90vh] overflow-y-auto p-8">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setState(s => ({ ...s, currentStep: 2.8 }))}
              className="text-txt-secondary hover:text-txt flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
            </button>
            <span className="text-sm text-txt-tertiary">Schritt 2.5 von 5</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-2">🎯 Ihre Sales-Strategie</h2>
            <p className="text-txt-secondary">Verifizieren Sie, wie wir Ihr Geschäftsmodell verstanden haben</p>
          </div>

          {!hasStrategy ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-info-soft rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-xl font-bold mb-2">Sales-Strategie analysieren</h3>
              <p className="text-txt-secondary mb-6">Wir analysieren Ihre Produkte, Zielgruppen und Conversion-Points</p>
              <button
                onClick={handleExtractSalesStrategy}
                disabled={state.isAnalyzing}
                className="px-8 py-3 bg-accent text-txt-inverted rounded-xl hover:bg-accent-bright font-bold shadow-card disabled:opacity-50 flex items-center justify-center mx-auto gap-2"
              >
                {state.isAnalyzing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Analysiere...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Jetzt analysieren
                  </>
                )}
              </button>
              <button
                onClick={() => setState(s => ({ ...s, currentStep: 3 }))}
                className="mt-4 text-txt-tertiary hover:text-txt-secondary text-sm"
              >
                Überspringen →
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Business Model & Target Segment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold text-txt mb-3 flex items-center">
                    <Building2 className="w-4 h-4 mr-2 text-accent" />
                    Geschäftsmodell
                  </h3>
                  <div className="space-y-2">
                    {(['b2b', 'b2c', 'b2b2c'] as const).map(model => (
                      <label key={model} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-bg-surface">
                        <input
                          type="radio"
                          name="businessModel"
                          checked={state.salesStrategy?.businessModel === model}
                          onChange={() => setState(s => ({
                            ...s,
                            salesStrategy: s.salesStrategy ? { ...s.salesStrategy, businessModel: model } : undefined
                          }))}
                          className="w-4 h-4 text-accent mr-3"
                        />
                        <span className="font-medium">{model.toUpperCase()}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-txt mb-3 flex items-center">
                    <Users className="w-4 h-4 mr-2 text-success" />
                    Zielgruppen-Segment
                  </h3>
                  <div className="space-y-2">
                    {(['consumer', 'startup', 'smb', 'mid_market', 'enterprise', 'mixed'] as const).map(segment => {
                      const labels = {
                        consumer: '👤 Endverbraucher (B2C)',
                        startup: '🚀 Startups',
                        smb: '🏪 Kleine/Mittlere Unternehmen',
                        mid_market: '🏢 Mittelstand (500-2000 MA)',
                        enterprise: '🏛️ Enterprise (2000+ MA)',
                        mixed: '🎯 Gemischt'
                      };
                      return (
                        <label key={segment} className="flex items-center p-2 border rounded-lg cursor-pointer hover:bg-bg-surface">
                          <input
                            type="radio"
                            name="targetSegment"
                            checked={state.salesStrategy?.targetAudienceSegment === segment}
                            onChange={() => setState(s => ({
                              ...s,
                              salesStrategy: s.salesStrategy ? { ...s.salesStrategy, targetAudienceSegment: segment } : undefined
                            }))}
                            className="w-4 h-4 text-success mr-3"
                          />
                          <span className="text-sm">{labels[segment]}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Target Audience Description */}
              {state.salesStrategy?.targetAudienceDescription && (
                <div>
                  <h3 className="font-bold text-txt mb-2">📊 Zielgruppen-Beschreibung</h3>
                  <p className="text-sm text-txt-secondary bg-bg-surface p-4 rounded-lg">{state.salesStrategy.targetAudienceDescription}</p>
                </div>
              )}

              {/* Products/Services */}
              {state.salesStrategy?.products && state.salesStrategy.products.length > 0 && (
                <div>
                  <h3 className="font-bold text-txt mb-3 flex items-center">
                    <Package className="w-4 h-4 mr-2 text-warning" />
                    Ihre Produkte/Services ({state.salesStrategy.products.filter(p => p.selected).length} ausgewählt)
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {state.salesStrategy.products.map((product, idx) => (
                      <label key={idx} className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-bg-surface">
                        <input
                          type="checkbox"
                          checked={product.selected}
                          onChange={() => setState(s => ({
                            ...s,
                            salesStrategy: s.salesStrategy ? {
                              ...s.salesStrategy,
                              products: s.salesStrategy.products.map((p, i) => 
                                i === idx ? { ...p, selected: !p.selected } : p
                              )
                            } : undefined
                          }))}
                          className="w-4 h-4 text-warning rounded mt-0.5 mr-3"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-txt">{product.name}</div>
                          <div className="text-xs text-txt-secondary mt-1">{product.description}</div>
                          {product.pricing && (
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs font-semibold text-success">{product.pricing}</span>
                              {product.pricingModel && (
                                <span className="text-[10px] px-2 py-0.5 bg-success-soft text-success rounded">
                                  {product.pricingModel}
                                </span>
                              )}
                            </div>
                          )}
                          {product.url && (
                            <a href={product.url} target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline mt-1 inline-block">
                              {product.url}
                            </a>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Competitors Selection */}
              {state.deepResearch?.competitorInsights && state.deepResearch.competitorInsights.length > 0 && (
                <div>
                  <h3 className="font-bold text-txt mb-3 flex items-center">
                    <Target className="w-4 h-4 mr-2 text-danger" />
                    Hauptwettbewerber ({state.salesStrategy.selectedCompetitors.length} ausgewählt)
                  </h3>
                  <div className="space-y-2">
                    {state.deepResearch.competitorInsights.map((comp, idx) => {
                      const isSelected = state.salesStrategy?.selectedCompetitors.includes(comp.name);
                      return (
                        <label key={idx} className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-bg-surface">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => setState(s => ({
                              ...s,
                              salesStrategy: s.salesStrategy ? {
                                ...s.salesStrategy,
                                selectedCompetitors: isSelected
                                  ? s.salesStrategy.selectedCompetitors.filter(c => c !== comp.name)
                                  : [...s.salesStrategy.selectedCompetitors, comp.name]
                              } : undefined
                            }))}
                            className="w-4 h-4 text-danger rounded mt-0.5 mr-3"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-txt">{comp.name}</div>
                            {comp.website && <div className="text-xs text-txt-tertiary">{comp.website}</div>}
                            <div className="grid grid-cols-2 gap-2 mt-2">
                              <div className="text-xs">
                                <span className="text-success font-semibold">Stärken:</span> {comp.strengths[0]}
                              </div>
                              <div className="text-xs">
                                <span className="text-danger font-semibold">Schwächen:</span> {comp.weaknesses[0]}
                              </div>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Conversion Points */}
              {state.salesStrategy.conversionPoints.length > 0 && (
                <div>
                  <h3 className="font-bold text-txt mb-3 flex items-center">
                    <Zap className="w-4 h-4 mr-2 text-accent" />
                    Conversion-Points auf Website
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {state.salesStrategy.conversionPoints.map((point, idx) => (
                      <div key={idx} className="p-2 bg-accent-soft border border-accent rounded-lg text-xs">
                        <div className="font-semibold text-accent">{point.type.toUpperCase()}</div>
                        <div className="text-accent">{point.label}</div>
                        {point.url && <div className="text-accent truncate">{point.url}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ideal Customer Profile */}
              {state.salesStrategy.idealCustomerProfile && (
                <div>
                  <h3 className="font-bold text-txt mb-3">💼 Ideales Kundenprofil</h3>
                  <div className="grid grid-cols-2 gap-4 bg-bg-surface p-4 rounded-lg">
                    {state.salesStrategy.idealCustomerProfile.companySize && (
                      <div>
                        <div className="text-xs font-semibold text-txt-tertiary">Unternehmensgröße</div>
                        <div className="text-sm text-txt">{state.salesStrategy.idealCustomerProfile.companySize}</div>
                      </div>
                    )}
                    {state.salesStrategy.idealCustomerProfile.budget && (
                      <div>
                        <div className="text-xs font-semibold text-txt-tertiary">Budget</div>
                        <div className="text-sm text-txt">{state.salesStrategy.idealCustomerProfile.budget}</div>
                      </div>
                    )}
                    {state.salesStrategy.idealCustomerProfile.industry && state.salesStrategy.idealCustomerProfile.industry.length > 0 && (
                      <div className="col-span-2">
                        <div className="text-xs font-semibold text-txt-tertiary mb-1">Branchen</div>
                        <div className="flex flex-wrap gap-1">
                          {state.salesStrategy.idealCustomerProfile.industry.map((ind, i) => (
                            <span key={i} className="text-xs px-2 py-1 bg-info-soft text-accent rounded">{ind}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {state.salesStrategy.idealCustomerProfile.painPoints && state.salesStrategy.idealCustomerProfile.painPoints.length > 0 && (
                      <div className="col-span-2">
                        <div className="text-xs font-semibold text-txt-tertiary mb-1">Pain Points</div>
                        <ul className="text-sm text-txt-secondary space-y-1">
                          {state.salesStrategy.idealCustomerProfile.painPoints.map((pain, i) => (
                            <li key={i} className="flex items-start">
                              <span className="text-danger mr-2">•</span>
                              {pain}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-6 border-t border-default">
                <button
                  onClick={handleExtractSalesStrategy}
                  className="px-6 py-2 border border-strong rounded-lg hover:bg-bg-surface text-txt-secondary"
                >
                  🔄 Neu analysieren
                </button>
                <button
                  onClick={() => setState(s => ({ ...s, currentStep: 3 }))}
                  className="px-8 py-3 bg-accent text-txt-inverted rounded-xl hover:bg-accent-bright font-bold shadow-card flex items-center"
                >
                  Weiter zu Modulen <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================================================
  // Step 3: Module Selection
  // ============================================================================

  const renderStep3 = () => {
    const { required, recommended, optional } = getModulesForIndustry(state.industry);
    const industryNames = {
      automotive: 'Autohäuser',
      insurance: 'Versicherungen',
      saas: 'SaaS',
      consulting: 'Professional Services',
      ecommerce: 'E-Commerce',
      agency: 'Marketing Agenturen',
      finance: 'Banken & Investments'
    };

    const toggleModule = (moduleId: string) => {
      if (moduleId === 'crm_core') return;  // Can't toggle required module
      
      setState(s => {
        const isSelected = s.selectedModules.includes(moduleId);
        const newModules = isSelected
          ? s.selectedModules.filter(id => id !== moduleId)
          : [...s.selectedModules, moduleId];
        
        return {
          ...s,
          selectedModules: newModules,
          totalPrice: calculateTotalPrice(newModules)
        };
      });
    };

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-hidden">
          <div className="bg-bg-card rounded-2xl shadow-heavy w-full max-w-5xl max-h-[90vh] flex flex-col my-8 mx-4 md:mx-0">
            {/* Header - Fixed */}
            <div className="p-6 md:p-8 pb-4 border-b border-subtle">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setState(s => ({ ...s, currentStep: 2.9 }))}
                  className="text-txt-secondary hover:text-txt flex items-center"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
                </button>
                <span className="text-sm text-txt-tertiary">Schritt 3 von 5</span>
              </div>

              <h2 className="text-xl md:text-2xl font-bold mb-1">💼 Wählen Sie Ihre Module</h2>
              <p className="text-txt-secondary text-sm md:text-base">Basierend auf "{industryNames[state.industry]}"</p>
            </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-8 pt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column: Required & Recommended */}
              <div className="space-y-6">
                {/* Required */}
                <div>
                  <h3 className="font-bold text-txt mb-3 flex items-center">
                    <Shield className="w-4 h-4 mr-2 text-accent" />
                    BASIS-MODUL (verpflichtend)
                  </h3>
                  {required.map(module => (
                    <div key={module.id} className="p-4 border-2 border-accent bg-info-soft rounded-xl mb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-accent mr-3 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="font-semibold text-txt">{module.name} <span className="text-accent">€{module.price}/Monat</span></div>
                            <ul className="text-sm text-txt-secondary mt-2 space-y-1">
                              {module.features.map((f, i) => (
                                <li key={i} className="flex items-center">
                                  <span className="w-1 h-1 bg-accent/80 rounded-full mr-2" />
                                  {f}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Recommended */}
                {recommended.length > 0 && (
                  <div>
                    <h3 className="font-bold text-txt mb-3 flex items-center">
                      <Sparkles className="w-4 h-4 mr-2 text-warning" />
                      EMPFOHLEN FÜR {industryNames[state.industry].toUpperCase()}
                    </h3>
                    <div className="space-y-3">
                      {recommended.map(module => {
                        const isSelected = state.selectedModules.includes(module.id);
                        const reason = getRecommendationReason(module.id, state.industry) || module.description;
                        
                        return (
                          <div
                            key={module.id}
                            onClick={() => toggleModule(module.id)}
                            className={`p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                              isSelected ? 'border-accent bg-info-soft shadow-card' : 'border-default hover:border-strong hover:shadow-card'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start flex-1">
                                <div className={`w-5 h-5 border-2 rounded mr-3 flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                                  isSelected ? 'border-accent bg-accent' : 'border-strong'
                                }`}>
                                  {isSelected && <CheckCircle2 className="w-4 h-4 text-txt-inverted" />}
                                </div>
                                <div className="flex-1">
                                  <div className="flex justify-between items-start">
                                    <span className="font-semibold text-txt">{module.name}</span>
                                    <span className="text-txt-secondary font-medium">€{module.price}<span className="text-xs font-normal">/Monat</span></span>
                                  </div>
                                  <p className="text-sm text-accent mt-1 bg-info-soft/50 p-2 rounded-lg inline-block">
                                    💡 {reason}
                                  </p>
                                  <ul className="text-sm text-txt-secondary mt-3 space-y-1">
                                    {module.features.map((f, i) => (
                                      <li key={i} className="flex items-center">
                                        <span className={`w-1 h-1 rounded-full mr-2 ${isSelected ? 'bg-accent/80' : 'bg-bg-surface-hover'}`} />
                                        {f}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Optional */}
              <div>
                {optional.length > 0 && (
                  <div className="h-full">
                    <h3 className="font-bold text-txt mb-3 flex items-center">
                      <Package className="w-4 h-4 mr-2 text-txt-tertiary" />
                      WEITERE MODULE (optional)
                    </h3>
                    <div className="grid grid-cols-1 gap-3">
                      {optional.map(module => {
                        const isSelected = state.selectedModules.includes(module.id);
                        return (
                          <div
                            key={module.id}
                            onClick={() => toggleModule(module.id)}
                            className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 ${
                              isSelected ? 'border-accent bg-info-soft shadow-card' : 'border-default hover:border-strong hover:bg-bg-surface'
                            }`}
                          >
                            <div className="flex items-start">
                              <div className={`w-5 h-5 border-2 rounded mr-3 flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                                isSelected ? 'border-accent bg-accent' : 'border-strong'
                              }`}>
                                {isSelected && <CheckCircle2 className="w-4 h-4 text-txt-inverted" />}
                              </div>
                              <div className="flex-1">
                                <div className="flex justify-between items-center mb-1">
                                  <span className="font-medium text-txt">{module.name}</span>
                                  <span className="text-sm text-txt-secondary">€{module.price}/Monat</span>
                                </div>
                                <p className="text-xs text-txt-tertiary line-clamp-2">{module.description}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

            {/* Footer - Fixed */}
            <div className="p-4 md:p-6 border-t border-default bg-bg-surface/50 rounded-b-2xl">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="w-full md:w-auto text-center md:text-left">
                  <div className="flex items-baseline justify-center md:justify-start gap-2">
                    <span className="text-sm text-txt-secondary">Gesamtbetrag:</span>
                    <span className="text-2xl font-bold text-accent">€{state.totalPrice}</span>
                    <span className="text-sm text-txt-tertiary">/ Monat</span>
                  </div>
                  <div className="flex items-center justify-center md:justify-start text-success text-sm mt-1">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" />
                    30 Tage kostenlos testen
                  </div>
                </div>

                <button
                  onClick={() => setState(s => ({ 
                    ...s, 
                    currentStep: 4,
                    // Ensure templateId is set based on current industry
                    templateId: s.templateId || s.industry 
                  }))}
                  className="w-full md:w-auto px-8 py-3 bg-accent text-txt-inverted font-semibold rounded-xl hover:bg-accent-bright transition-colors flex items-center justify-center shadow-card shadow-glow-accent"
                >
                  Module bestätigen <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Step 4: Template & Team
  // ============================================================================

  const renderStep4 = () => {
    // Fallback: if templateId is not set, use current industry
    const effectiveTemplateId = state.templateId || state.industry;
    const template = getTemplateById(effectiveTemplateId);
    
    if (!template) {
      console.error('No template found for:', effectiveTemplateId);
      // Auto-fix: set templateId to industry and retry
      setState(s => ({ ...s, templateId: s.industry }));
      return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-card rounded-2xl shadow-heavy max-w-md w-full p-8 text-center">
            <Loader2 className="w-12 h-12 text-accent animate-spin mx-auto mb-4" />
            <p className="text-txt-secondary">Vorlage wird geladen...</p>
          </div>
        </div>
      );
    }

    const industryIcons = {
      automotive: Car,
      insurance: Shield,
      saas: Cloud,
      consulting: Users,
      ecommerce: Mail,  // Using Mail as placeholder for ShoppingCart
      agency: Edit3,  // Palette would be better but using Edit3 for now
      finance: Euro  // Using Euro for finance
    };
    const Icon = industryIcons[state.industry];

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-bg-card rounded-2xl shadow-heavy max-w-2xl w-full p-8 my-8">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setState(s => ({ ...s, currentStep: 3 }))}
              className="text-txt-secondary hover:text-txt flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
            </button>
            <span className="text-sm text-txt-tertiary">Schritt 4 von 5</span>
          </div>

          <h2 className="text-2xl font-bold mb-6">🎨 CRM-Konfiguration & Team</h2>

          {/* Template Preview */}
          <div className="mb-6">
            <div className="flex items-center mb-3">
              {Icon && <Icon className="w-6 h-6 mr-2 text-accent" />}
              <h3 className="font-bold text-txt">{template.name.toUpperCase()} VORLAGE</h3>
            </div>
            
            <div className="space-y-2 text-sm text-txt-secondary">
              <div className="flex items-start">
                <CheckCircle2 className="w-4 h-4 text-success mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">{template.defaultProducts.length} vordefinierte {template.id === 'ecommerce' ? 'Interaktionstypen' : template.id === 'agency' ? 'Marketing-Services' : template.id === 'finance' ? 'Finanzprodukte' : 'Produkte'}</span>
                  <div className="text-xs text-txt-tertiary ml-6">
                    {template.id === 'automotive' && '• 12 Fahrzeugkategorien • 8 Service-Leistungen • 4 Teile & Zubehör'}
                    {template.id === 'insurance' && '• 7 Versicherungstypen'}
                    {template.id === 'saas' && '• 4 Subscription-Tiers • 4 Add-ons'}
                    {template.id === 'consulting' && '• 4 Consulting-Kategorien • 4 Professional Services'}
                    {template.id === 'ecommerce' && '• Kundenbeziehungen • Anfragen • Bestellungen • Support'}
                    {template.id === 'agency' && '• Social Media • Google Ads • SEO • Content • Brand Strategy'}
                    {template.id === 'finance' && '• Konten • Kredite • Baufinanzierung • Investment-Beratung'}
                  </div>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="w-4 h-4 text-success mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">Optimierte Verkaufs-Pipeline</span>
                  <div className="text-xs text-txt-tertiary">
                    {template.leadStages.join(' → ')}
                  </div>
                </div>
              </div>
            </div>

            {/* Sample Data */}
            {template.sampleData.available && (
              <div className="mt-4 p-4 bg-bg-surface rounded-lg">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={state.includeSampleData}
                    onChange={(e) => setState(s => ({ ...s, includeSampleData: e.target.checked }))}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <div className="font-medium text-txt">Beispieldaten einschließen</div>
                    <div className="text-sm text-txt-secondary mt-1">Enthält:</div>
                    <ul className="text-xs text-txt-tertiary mt-1 ml-4 space-y-0.5">
                      {template.sampleData.includes.map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                    <p className="text-xs text-accent mt-2">💡 Zum Ausprobieren & Lernen</p>
                  </div>
                </label>
              </div>
            )}
          </div>

          {/* Team Invites */}
          <div className="mb-6 p-4 border-2 border-dashed border-strong rounded-lg">
            <div className="flex items-center mb-2">
              <Users className="w-5 h-5 text-txt-secondary mr-2" />
              <h3 className="font-bold text-txt">Team einladen (optional)</h3>
            </div>
            <p className="text-sm text-txt-secondary mb-3">👥 Kollegen direkt einladen</p>
            <textarea
              value={state.teamInvites}
              onChange={(e) => setState(s => ({ ...s, teamInvites: e.target.value }))}
              placeholder="max.mueller@firma.de, lisa.schmidt@firma.de"
              rows={2}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
            <p className="text-xs text-txt-tertiary mt-1">ℹ️ Mehrere E-Mails mit Komma trennen</p>
            <p className="text-xs text-txt-tertiary mt-1">Rolle: Manager (Kann später geändert werden)</p>
          </div>

          <div className="bg-info-soft border border-accent rounded-lg p-4 mb-6">
            <p className="text-sm text-info font-medium">✨ Bereit? Ihr CRM wird jetzt eingerichtet!</p>
          </div>

          <button
            onClick={() => setState(s => ({ ...s, currentStep: 5 }))}
            className="w-full bg-accent text-txt-inverted py-3 rounded-lg hover:bg-accent-bright flex items-center justify-center"
          >
            CRM erstellen <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Step 5: Creating Organization
  // ============================================================================

  const handleCreateOrganization = async () => {
    setState(s => ({ ...s, isCreating: true, creationProgress: [] }));

    try {
      const addProgress = (msg: string) => {
        setState(s => ({ ...s, creationProgress: [...s.creationProgress, msg] }));
      };

      // Create organization
      addProgress('Organisation erstellt');
      
      // Prepare address data
      const addressData = state.analysisResult?.business.location ? {
        street: state.analysisResult.business.location.address || '',
        city: state.analysisResult.business.location.city || '',
        country: state.analysisResult.business.location.country || '',
        postalCode: '' // Not always extracted reliably
      } : undefined;

      const selectedGeneratedImage = state.selectedImageIndex !== -1 
        ? state.generatedImages[state.selectedImageIndex]
        : null;

      // Build onboardingData - NO undefined values allowed in Firestore!
      const onboardingData: Record<string, any> = {
        websiteUrl: state.websiteUrl,
        onboardingCompletedAt: new Date(),
        
        // Save only user-selected items
        detectedOfferings: state.selectedOfferings || [],
        keywords: state.selectedKeywords || [],
        brandColors: state.selectedColorIndices.map(i => state.analysisResult?.analysis.brandColors?.[i]).filter(Boolean) || [],
        brandImages: state.selectedScrapedImageIndices.map(i => state.brandImages[i]).filter(Boolean) || [],
        
        // Arrays with defaults
        competitors: state.analysisResult?.analysis.competitors || [],
        brandFonts: state.analysisResult?.analysis.brandFonts || []
      };
      
      // Add optional fields only if they exist
      if (state.analysisResult?.business.industry) {
        onboardingData.detectedIndustry = state.analysisResult.business.industry;
      }
      if (state.analysisResult?.business.industryConfidence) {
        onboardingData.industryConfidence = state.analysisResult.business.industryConfidence;
      }
      if (state.analysisResult?.scraping.scrapedAt) {
        onboardingData.scrapedAt = state.analysisResult.scraping.scrapedAt;
      }
      if (state.analysisResult?.analysis.usp) {
        onboardingData.usp = state.analysisResult.analysis.usp;
      }
      if (state.analysisResult?.analysis.slogan) {
        onboardingData.slogan = state.analysisResult.analysis.slogan;
      }
      if (state.analysisResult?.analysis.targetAudience) {
        onboardingData.targetAudience = state.analysisResult.analysis.targetAudience;
      }
      if (state.analysisResult?.analysis.brandTone) {
        onboardingData.brandTone = state.analysisResult.analysis.brandTone;
      }
      
      // Add deep research data if available
      if (state.deepResearch) {
        onboardingData.deepResearch = state.deepResearch;
      }
      
      // Add sales strategy data if available
      if (state.salesStrategy) {
        onboardingData.salesStrategy = {
          businessModel: state.salesStrategy.businessModel,
          targetAudienceSegment: state.salesStrategy.targetAudienceSegment,
          targetAudienceDescription: state.salesStrategy.targetAudienceDescription,
          selectedProducts: state.salesStrategy.products.filter(p => p.selected),
          allProducts: state.salesStrategy.products,
          conversionPoints: state.salesStrategy.conversionPoints,
          idealCustomerProfile: state.salesStrategy.idealCustomerProfile,
          selectedCompetitors: state.salesStrategy.selectedCompetitors
        };
      }

      if (selectedGeneratedImage?.style) {
        onboardingData.selectedBrandImageStyle = selectedGeneratedImage.style;
      }

      // Add generated image to brandImages if selected
      if (selectedGeneratedImage?.url) {
        onboardingData.brandImages = [selectedGeneratedImage.url, ...(onboardingData.brandImages || [])];
      }

      // Get the user-selected logo
      const selectedLogo = state.analysisResult?.analysis.logoCandidates?.[state.selectedLogoIndex]?.url 
        || state.analysisResult?.analysis.logoUrl 
        || null;

      // Build orgData - Firebase doesn't allow undefined values!
      const orgData: any = {
        name: state.orgName,
        industry: state.industry,
        billingType: 'module_based' as const,
        createdBy: user.uid,
        website: state.websiteUrl,
        socialProfiles: state.analysisResult?.analysis.socialMedia || {},
        onboardingData
      };
      
      // Add optional fields only if they have values
      if (state.analysisResult?.business.contact?.email) {
        orgData.email = state.analysisResult.business.contact.email;
      }
      if (state.analysisResult?.business.contact?.phone) {
        orgData.phone = state.analysisResult.business.contact.phone;
      }
      if (addressData && (addressData.street || addressData.city || addressData.country)) {
        orgData.address = addressData;
      }
      const logoUrl = selectedGeneratedImage?.url ?? selectedLogo;
      if (logoUrl) {
        orgData.logo = logoUrl;
      }
      
      const orgId = await organizationService.createOrganization(orgData, user.uid);
      
      // Activate modules
      addProgress(`Module aktiviert (${state.selectedModules.length})`);
      await moduleService.activateModules(orgId, state.selectedModules, true);
      
      // Apply template - use industry as fallback if templateId is not set
      const effectiveTemplateId = state.templateId || state.industry;
      addProgress(`${state.industry.charAt(0).toUpperCase() + state.industry.slice(1)}-Template angewandt`);
      await onboardingTemplateService.applyTemplateToOrganization(orgId, effectiveTemplateId, {
        includeSampleData: state.includeSampleData,
        userId: user.uid
      });
      
      // Send team invites
      if (state.teamInvites.trim()) {
        const emails = state.teamInvites.split(',').map(e => e.trim()).filter(e => e);
        addProgress(`Team-Einladungen versenden (${emails.length})`);
        const result = await usersService.bulkCreateInvites(orgId, emails, user.uid, 'manager');
        console.log(`✅ Sent ${result.sent.length} invites, ${result.failed.length} failed`);
      }
      
      addProgress('Abgeschlossen');
      
      // Move to success screen
      setTimeout(() => {
        setState(s => ({ ...s, currentStep: 6, isCreating: false }));
      }, 1000);
      
    } catch (error) {
      console.error('Error creating organization:', error);
      setState(s => ({ ...s, isCreating: false }));
    }
  };

  React.useEffect(() => {
    if (state.currentStep === 5 && !state.isCreating && state.creationProgress.length === 0) {
      handleCreateOrganization();
    }
  }, [state.currentStep]);

  const renderStep5 = () => {
    const progress = (state.creationProgress.length / 5) * 100;

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-bg-card rounded-2xl shadow-heavy max-w-lg w-full p-8">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-accent animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">🚀 Ihr CRM wird eingerichtet...</h2>
            
            <div className="w-full bg-bg-surface-hover rounded-full h-2 mb-6">
              <div
                className="bg-accent h-2 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>

            <div className="space-y-2 text-left">
              {state.creationProgress.map((msg, i) => (
                <div key={i} className="flex items-center text-success">
                  <CheckCircle2 className="w-5 h-5 mr-2" /> {msg}
                </div>
              ))}
              {state.creationProgress.length < 5 && (
                <div className="flex items-center text-txt-muted">
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" /> 
                  {state.creationProgress.length === 0 && 'Organisation erstellen...'}
                  {state.creationProgress.length === 1 && 'Module aktivieren...'}
                  {state.creationProgress.length === 2 && 'Template anwenden...'}
                  {state.creationProgress.length === 3 && 'Einladungen versenden...'}
                  {state.creationProgress.length === 4 && 'Finalisieren...'}
                </div>
              )}
            </div>

            <p className="text-sm text-txt-tertiary mt-4">⏱️ Noch ca. 10 Sekunden</p>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Step 6: Success!
  // ============================================================================

  const renderStep6 = () => {
    const template = getTemplateById(state.templateId);
    
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-bg-card rounded-2xl shadow-heavy max-w-2xl w-full p-8 my-8">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold text-txt mb-2">Geschafft!</h2>
            <p className="text-lg text-txt-secondary">Ihr CRM ist einsatzbereit</p>
          </div>

          <div className="space-y-4 mb-6">
            <div className="p-4 bg-bg-surface rounded-lg">
              <div className="text-sm text-txt-secondary mb-2">✅ Organisation: {state.orgName}</div>
              <div className="text-sm text-txt-secondary">✅ Branche: {template?.name}</div>
              {state.analysisResult?.analysis.competitors && state.analysisResult.analysis.competitors.length > 0 && (
                <div className="mt-3 pt-3 border-t border-default">
                  <div className="text-xs font-bold text-txt-tertiary uppercase mb-1">🔍 Analyse-Ergebnisse</div>
                  <div className="text-sm text-txt-secondary">
                    • {state.analysisResult.analysis.competitors.length} Wettbewerber identifiziert
                  </div>
                  <div className="text-sm text-txt-secondary">
                    • {state.analysisResult.analysis.keywords?.length || 0} Keywords für Google Ads gefunden
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 bg-info-soft rounded-lg">
              <div className="font-bold text-txt mb-2">💼 AKTIVIERTE MODULE</div>
              {state.selectedModules.map(moduleId => {
                const mod = getModulesForIndustry(state.industry);
                const allMods = [...mod.required, ...mod.recommended, ...mod.optional];
                const module = allMods.find(m => m.id === moduleId);
                return (
                  <div key={moduleId} className="text-sm text-txt-secondary">
                    • {module?.name} €{module?.price}/Monat
                  </div>
                );
              })}
              <div className="mt-2 pt-2 border-t border-accent">
                <div className="font-bold text-info">💰 Gesamt: €{state.totalPrice} / Monat</div>
                <div className="text-sm text-success">🎁 30 Tage kostenlos</div>
              </div>
            </div>

            <div className="p-4 bg-success-soft rounded-lg">
              <div className="font-bold text-txt mb-2">📦 SETUP ABGESCHLOSSEN</div>
              <div className="text-sm text-txt-secondary">• {template?.defaultProducts.length} Produkte konfiguriert</div>
              <div className="text-sm text-txt-secondary">• Verkaufs-Pipeline eingerichtet</div>
              {state.includeSampleData && <div className="text-sm text-txt-secondary">• Beispieldaten erstellt</div>}
              {state.teamInvites && <div className="text-sm text-txt-secondary">• Team-Einladungen versendet</div>}
            </div>

            <div className="p-4 border-2 border-dashed border-accent rounded-lg">
              <div className="font-bold text-txt mb-2">💡 NÄCHSTE SCHRITTE</div>
              <ol className="text-sm text-txt-secondary space-y-1">
                <li>1. Dashboard erkunden</li>
                <li>2. Erste Leads erfassen</li>
                <li>3. Google Ads verbinden (optional)</li>
              </ol>
            </div>
          </div>

          <button
            onClick={onComplete}
            className="w-full bg-accent text-txt-inverted py-3 rounded-lg hover:bg-accent-bright flex items-center justify-center font-bold"
          >
            Zum Dashboard <ArrowRight className="w-5 h-5 ml-2" />
          </button>

          <button
            onClick={() => {/* TODO: Tour */}}
            className="w-full mt-2 text-accent py-2 hover:underline text-sm"
          >
            Oder: Kurze Tour starten (5 Min)
          </button>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Step 1.5: Scraping Selection (New)
  // ============================================================================

  const handleSelectExistingScraping = async (scraping: any, runDeepResearch: boolean = false) => {
    const data = scraping.onboardingData;
    
    // Reconstruct analysis result structure from saved data
    const analysisResult: WebsiteAnalysisResult = {
      scraping: {
        url: data.websiteUrl || '',
        scrapedAt: scraping.scrapedAt,
        success: true,
        markdown: '', // Not needed for re-use usually
        html: '', 
        images: [],
        metadata: { title: '', description: '' }
      },
      business: {
        name: scraping.name,
        nameConfidence: 100,
        industry: data.detectedIndustry,
        industryConfidence: data.industryConfidence || 100,
        industryReasoning: 'Reused from previous analysis',
        alternativeIndustries: [],
        location: { // Try to recover location if saved, otherwise generic
           city: '', country: '', address: '' 
        },
        contact: { email: '', phone: '' }
      },
      analysis: {
        suggestedTemplate: data.detectedIndustry,
        recommendedModules: [], // Will be recalculated
        detectedOfferings: data.detectedOfferings || [],
        businessModel: 'b2b', // Default if missing
        usp: data.usp,
        targetAudience: data.targetAudience,
        brandTone: data.brandTone,
        logoUrl: undefined, // We don't save the logo URL in onboardingData usually, might be on org
        brandColors: data.brandColors || [],
        brandFonts: data.brandFonts || [],
        socialMedia: undefined, // Might be lost if not saved deep
        competitors: data.competitors || [],
        keywords: data.keywords || []
      },
      quality: {
        overallScore: 100,
        hasValidContent: true,
        hasBusinessInfo: true,
        hasIndustryIndicators: true,
        recommendManualReview: false,
        issues: []
      }
    };

    // Update state with reused data
    setState(s => ({
      ...s,
      entryMethod: 'website',
      websiteUrl: data.websiteUrl || '',
      analysisResult: analysisResult,
      orgName: scraping.name,
      industry: data.detectedIndustry || 'consulting',
      industryConfidence: data.industryConfidence || 100,
      // recalculate based on industry
      templateId: data.detectedIndustry || 'consulting',
      selectedModules: ['crm_core', ...(onboardingAnalysisService as any).getRecommendedModulesForIndustry ? (onboardingAnalysisService as any).getRecommendedModulesForIndustry(data.detectedIndustry || 'consulting') : []], // Fallback logic needed or import helper
      totalPrice: 19, // Recalculate after
      
      currentStep: 2.8, // Jump directly to Stylebook
      showScrapingSelection: false,
      deepResearch: data.deepResearch || undefined // Load existing deep research if available
    }));
    
    // If user wants fresh deep research on existing scraping
    if (runDeepResearch && !data.deepResearch) {
      setState(s => ({
        ...s,
        backgroundProcessing: {
          ...s.backgroundProcessing,
          deepResearch: 'running'
        }
      }));
      
      try {
        const deepResearch = await onboardingAnalysisService.performDeepResearch(
          scraping.name,
          scraping.website,
          data.detectedIndustry || 'consulting',
          data.scrapedMarkdown || '',
          data.competitors || [],
          data.usp || '',
          data.detectedOfferings || []
        );
        
        setState(s => ({
          ...s,
          deepResearch,
          backgroundProcessing: {
            ...s.backgroundProcessing,
            deepResearch: 'complete'
          }
        }));
      } catch (error) {
        console.error('Deep research failed:', error);
        setState(s => ({
          ...s,
          backgroundProcessing: {
            ...s.backgroundProcessing,
            deepResearch: 'error'
          }
        }));
      }
    }
  };

  // Render wizard step content
  const renderWizardContent = () => {
    // Video + Background Processing Step (MUST be first!)
    if (state.currentStep === 2.1 && state.showIntroVideo) {
      // Check if ESSENTIAL processing is complete (Deep Research is optional, can run in background)
      const essentialComplete = 
        state.backgroundProcessing.scraping === 'complete' &&
        state.backgroundProcessing.logoExtraction === 'complete' &&
        state.backgroundProcessing.colorExtraction === 'complete';
      
      const allComplete = essentialComplete && state.backgroundProcessing.deepResearch === 'complete';
      
      const ProcessStatusBadge = ({ label, status, isLast }: { label: string; status: string; isLast?: boolean }) => (
        <div className={`flex items-center gap-2 text-sm ${isLast ? 'col-span-2' : ''}`}>
          {status === 'running' && <Loader2 className="w-4 h-4 text-info animate-spin" />}
          {status === 'complete' && <CheckCircle2 className="w-4 h-4 text-success" />}
          {status === 'error' && <AlertCircle className="w-4 h-4 text-danger" />}
          {status === 'pending' && <div className="w-4 h-4 rounded-full border-2 border-strong" />}
          <span className={`
            ${status === 'complete' ? 'text-success' : ''}
            ${status === 'running' ? 'text-info font-medium' : ''}
            ${status === 'error' ? 'text-danger' : ''}
            ${status === 'pending' ? 'text-txt-muted' : ''}
          `}>
            {label}
          </span>
        </div>
      );
      
      return (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
          <div className="w-full max-w-4xl p-4">
            {/* Video Player - Synthesia Embed */}
            <div className="relative bg-black rounded-xl overflow-hidden shadow-heavy">
              <div className="w-full aspect-video">
                <iframe
                  src="https://share.synthesia.io/embeds/videos/da8c4a30-a198-49ed-8830-1cb0a5fdee39?autoPlay=true&loop=false"
                  loading="eager"
                  title="Welcome to Business Hub"
                  allow="accelerometer; autoplay; encrypted-media; fullscreen"
                  className="w-full h-full border-0"
                  style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0 }}
                />
              </div>
              
              {/* Skip button - available when essential processing is complete */}
              {essentialComplete && (
                <button
                  onClick={() => {
                    // User wants to skip video - essential processing is done
                    setState(s => ({ ...s, showIntroVideo: false, currentStep: 2.8 }));
                  }}
                  className="absolute top-4 right-4 px-4 py-2 bg-black/60 hover:bg-black/70 text-txt-inverted text-sm rounded-lg backdrop-blur-sm transition-all z-10"
                >
                  {allComplete ? 'Weiter →' : 'Video überspringen →'}
                </button>
              )}
            </div>
            
            {/* Background Processing Status */}
            <div className="mt-4 bg-primary/90 backdrop-blur-sm rounded-lg p-4">
              <h3 className="text-txt-inverted font-semibold mb-3 text-sm">
                🔍 Hintergrund-Analyse läuft...
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <ProcessStatusBadge 
                  label="Website Scraping" 
                  status={state.backgroundProcessing.scraping} 
                />
                <ProcessStatusBadge 
                  label="Logo-Extraktion" 
                  status={state.backgroundProcessing.logoExtraction} 
                />
                <ProcessStatusBadge 
                  label="Farbanalyse" 
                  status={state.backgroundProcessing.colorExtraction} 
                />
                <ProcessStatusBadge 
                  label="Deep Research" 
                  status={state.backgroundProcessing.deepResearch} 
                  isLast={true}
                />
              </div>
              
              {allComplete && (
                <div className="mt-4 pt-4 border-t border-strong">
                  <p className="text-success text-sm font-medium">✓ Analyse abgeschlossen</p>
                  <p className="text-txt-muted text-xs mt-1">Das Video wird automatisch beendet oder klicken Sie "Überspringen"</p>
                </div>
              )}
              
              {state.backgroundError && (
                <div className="mt-4 pt-4 border-t border-danger/50">
                  <p className="text-danger text-sm">⚠️ {state.backgroundError}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
  
    if (state.showScrapingSelection) {
      return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-card rounded-2xl shadow-heavy max-w-2xl w-full p-8 max-h-[80vh] flex flex-col">
            <button
              onClick={() => setState(s => ({ ...s, showScrapingSelection: false }))}
              className="mb-4 text-txt-secondary hover:text-txt flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
            </button>
            
            <h2 className="text-2xl font-bold mb-2">Vorhandene Analyse nutzen</h2>
            <p className="text-txt-secondary mb-6">Wir haben bereits Daten für diese Websites gefunden. Möchten Sie eine davon nutzen?</p>
            
            <div className="flex-1 overflow-y-auto space-y-3 pr-2">
              {state.existingScrapings.map((scraping) => {
                const hasDeepResearch = scraping.onboardingData?.deepResearch;
                return (
                  <div key={scraping.id} className="border border-default rounded-xl p-4 hover:border-accent transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="font-bold text-txt">{scraping.name}</div>
                        <div className="text-sm text-txt-tertiary">{scraping.website}</div>
                        <div className="text-xs text-txt-muted mt-1 flex items-center gap-2">
                          📅 {scraping.scrapedAt.toLocaleDateString()}
                          {hasDeepResearch && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-accent-soft text-accent">
                              ✓ Deep Research vorhanden
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSelectExistingScraping(scraping, false)}
                        className="flex-1 px-3 py-2 bg-accent text-txt-inverted text-sm rounded-lg hover:bg-accent-bright transition-all flex items-center justify-center gap-1"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Nutzen
                      </button>
                      {!hasDeepResearch && (
                        <button
                          onClick={() => handleSelectExistingScraping(scraping, true)}
                          className="flex-1 px-3 py-2 bg-accent text-txt-inverted text-sm rounded-lg hover:bg-accent transition-all flex items-center justify-center gap-1"
                        >
                          <Sparkles className="w-3 h-3" />
                          + Deep Research
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              
              <button
                onClick={() => setState(s => ({ ...s, showScrapingSelection: false, entryMethod: 'website', currentStep: 2 }))}
                className="w-full p-4 border border-dashed border-strong rounded-xl hover:border-strong hover:bg-bg-surface transition-all text-left flex items-center justify-center text-txt-secondary mt-4"
              >
                <Globe className="w-4 h-4 mr-2" />
                Neue Website analysieren
              </button>
            </div>
          </div>
        </div>
      );
    }

    switch (state.currentStep) {
      case 1:
        return renderStep1();
      case 2:
      case 2.5:
        return renderStep2();
      case 2.8:
        // Stylebook Selection - ALWAYS show this screen at step 2.8
        return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
              <div className="bg-bg-card rounded-2xl shadow-heavy w-full max-w-5xl max-h-[90vh] overflow-y-auto flex flex-col md:flex-row">
                
                {/* Left: Brand DNA Styleguide */}
                <div className="w-full md:w-1/3 bg-bg-surface p-6 md:p-8 border-b md:border-b-0 md:border-r border-default flex-shrink-0">
                  <div className="mb-6">
                    <h2 className="text-xl font-bold text-txt mb-2">🧬 Brand DNA</h2>
                    <p className="text-sm text-txt-tertiary">
                      Wir haben diese Elemente aus Ihrer Website extrahiert, um konsistente Marketing-Inhalte zu erstellen.
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Logo Selection */}
                    <div>
                      <label className="text-xs font-bold text-txt-tertiary uppercase tracking-wider block mb-2">
                        Logo auswählen
                      </label>
                      {state.analysisResult?.analysis.logoCandidates && state.analysisResult.analysis.logoCandidates.length > 0 ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            {state.analysisResult.analysis.logoCandidates.slice(0, 4).map((candidate, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => setState(prev => ({ ...prev, selectedLogoIndex: idx }))}
                                className={`p-3 border-2 rounded-lg hover:border-accent transition ${
                                  state.selectedLogoIndex === idx 
                                    ? 'border-accent bg-info-soft' 
                                    : 'border-default bg-bg-card'
                                }`}
                              >
                                <img 
                                  src={candidate.url} 
                                  alt={candidate.altText || `Logo ${idx + 1}`} 
                                  className="w-full h-12 object-contain"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                                <div className="text-[10px] text-txt-tertiary mt-1 text-center">
                                  {candidate.source === 'clearbit' && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 bg-success-soft text-success font-semibold rounded">
                                      ✓ Clearbit
                                    </span>
                                  )}
                                  {candidate.source === 'google' && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 bg-info-soft text-accent font-semibold rounded">
                                      ✓ Google
                                    </span>
                                  )}
                                  {candidate.source === 'scraped' && (
                                    <span className="text-txt-muted">
                                      {candidate.format.toUpperCase()} · {candidate.location}
                                    </span>
                                  )}
                                  {!candidate.source && (
                                    <span className="text-txt-muted">
                                      {candidate.format.toUpperCase()}
                                    </span>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                          <div className="text-xs text-txt-muted mt-1">
                            {state.analysisResult.analysis.logoCandidates.length > 4 && 
                              `+${state.analysisResult.analysis.logoCandidates.length - 4} weitere`
                            }
                          </div>
                        </div>
                      ) : state.analysisResult?.analysis.logoUrl ? (
                        <div className="p-4 bg-bg-card rounded-lg border border-default inline-block">
                          <img 
                            src={state.analysisResult.analysis.logoUrl} 
                            alt="Extracted Logo" 
                            className="h-12 object-contain"
                          />
                        </div>
                      ) : (
                        <div className="text-sm text-txt-muted italic">Kein Logo gefunden</div>
                      )}
                    </div>

                    {/* Slogan */}
                    <div>
                      <label className="text-xs font-bold text-txt-tertiary uppercase tracking-wider block mb-2">
                        Slogan
                      </label>
                      {state.analysisResult?.analysis.slogan ? (
                        <p className="text-sm text-txt-secondary italic">"{state.analysisResult.analysis.slogan}"</p>
                      ) : (
                        <span className="text-sm text-txt-muted italic">Kein Slogan gefunden</span>
                      )}
                    </div>

                    {/* USP */}
                    <div>
                      <label className="text-xs font-bold text-txt-tertiary uppercase tracking-wider block mb-2">
                        USP (Alleinstellungsmerkmal)
                      </label>
                      {state.analysisResult?.analysis.usp ? (
                        <p className="text-sm text-txt-secondary">{state.analysisResult.analysis.usp}</p>
                      ) : (
                        <span className="text-sm text-txt-muted italic">Kein USP gefunden</span>
                      )}
                    </div>

                    {/* Offerings */}
                    <div>
                      <label className="text-xs font-bold text-txt-tertiary uppercase tracking-wider block mb-2">
                        Gefundene Angebote
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {state.analysisResult?.analysis.detectedOfferings && state.analysisResult.analysis.detectedOfferings.length > 0 ? (
                          state.analysisResult.analysis.detectedOfferings.slice(0, 5).map((offering, i) => (
                            <span key={i} className="px-2 py-1 bg-bg-surface text-txt-secondary text-xs rounded-md">
                              {offering}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-txt-muted italic">Keine Angebote erkannt</span>
                        )}
                      </div>
                    </div>

                    {/* Keywords */}
                    <div>
                      <label className="text-xs font-bold text-txt-tertiary uppercase tracking-wider block mb-2">
                        Schlagworte
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {state.analysisResult?.analysis.keywords && state.analysisResult.analysis.keywords.length > 0 ? (
                          state.analysisResult.analysis.keywords.slice(0, 8).map((keyword, i) => (
                            <span key={i} className="text-xs text-txt-tertiary bg-bg-surface px-2 py-1 rounded border border-subtle">
                              #{keyword}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-txt-muted italic">Keine Keywords</span>
                        )}
                      </div>
                    </div>

                    {/* Competitors */}
                    <div>
                      <label className="text-xs font-bold text-txt-tertiary uppercase tracking-wider block mb-2">
                        Wettbewerber
                      </label>
                      <div className="space-y-1">
                        {state.analysisResult?.analysis.competitors && state.analysisResult.analysis.competitors.length > 0 ? (
                          state.analysisResult.analysis.competitors.slice(0, 3).map((comp, i) => (
                            <div key={i} className="text-sm text-txt-secondary flex items-center">
                              <Users className="w-3 h-3 mr-2 text-txt-muted" />
                              {comp}
                            </div>
                          ))
                        ) : (
                          <span className="text-sm text-txt-muted italic">Keine Wettbewerber gefunden</span>
                        )}
                      </div>
                    </div>

                    {/* Brand Colors */}
                    <div>
                      <label className="text-xs font-bold text-txt-tertiary uppercase tracking-wider block mb-2">
                        Farbpalette
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {state.analysisResult?.analysis.brandColors && state.analysisResult.analysis.brandColors.length > 0 ? (
                          state.analysisResult.analysis.brandColors.slice(0, 5).map((color, i) => (
                            <div key={i} className="group relative">
                              <div 
                                className="w-8 h-8 rounded-full border border-default shadow-card cursor-help" 
                                style={{ backgroundColor: color }} 
                              />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-bg-card text-txt-inverted text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                                {color}
                              </span>
                            </div>
                          ))
                        ) : (
                          <span className="text-sm text-txt-muted italic">Keine Farben extrahiert</span>
                        )}
                      </div>
                    </div>

                    {/* Typography */}
                    <div>
                      <label className="text-xs font-bold text-txt-tertiary uppercase tracking-wider block mb-2">
                        Typografie
                      </label>
                      <div className="space-y-1">
                        {state.analysisResult?.analysis.brandFonts && state.analysisResult.analysis.brandFonts.length > 0 ? (
                          state.analysisResult.analysis.brandFonts.slice(0, 3).map((font, i) => (
                            <div key={i} className="text-sm text-txt-secondary font-medium bg-bg-card px-2 py-1 rounded border border-subtle">
                              Aa {font}
                            </div>
                          ))
                        ) : (
                          <span className="text-sm text-txt-muted italic">Standard-Fonts</span>
                        )}
                      </div>
                    </div>

                    {/* Brand Tone */}
                    <div>
                      <label className="text-xs font-bold text-txt-tertiary uppercase tracking-wider block mb-2">
                        Tonalität
                      </label>
                      <div className="inline-flex items-center px-3 py-1.5 bg-bg-card border border-default rounded-lg text-sm font-medium text-txt-secondary">
                        {state.analysisResult?.analysis.brandTone === 'formal' ? '👔 Formell (Sie)' : '👋 Locker (Du)'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: AI Image Generation */}
                <div className="w-full md:w-2/3 p-6 md:p-8 flex flex-col">
                  {state.isGeneratingImages ? (
                    <div className="flex-1 flex flex-col items-center justify-center py-12 text-center">
                      <div className="relative w-24 h-24 mb-6">
                        <div className="absolute inset-0 border-4 border-subtle rounded-full animate-pulse"></div>
                        <div className="absolute inset-0 border-4 border-t-accent rounded-full animate-spin"></div>
                        <Sparkles className="absolute inset-0 m-auto w-8 h-8 text-accent animate-bounce" />
                      </div>
                      <h2 className="text-2xl font-bold mb-2 text-txt">Künstliche Intelligenz arbeitet... 🎨</h2>
                      <p className="text-txt-secondary mb-6 max-w-md mx-auto">
                        Wir generieren einzigartige Markenbilder, die Ihre extrahierte DNA (Farben, Stil, Industrie) perfekt widerspiegeln.
                      </p>
                      <p className="text-sm text-txt-tertiary animate-pulse bg-bg-surface px-4 py-2 rounded-full">
                        Dies kann bis zu einer Minute dauern...
                      </p>
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h2 className="text-2xl font-bold text-txt mb-1">Stylebook Selection 🎨</h2>
                          <p className="text-txt-secondary text-sm">Wählen Sie die Elemente, die Ihre Marke definieren.</p>
                        </div>
                        <button
                          onClick={() => setState(s => ({ ...s, currentStep: 2, analysisResult: undefined, showIntroVideo: false }))}
                          className="text-txt-tertiary hover:text-txt-secondary md:hidden"
                          title="Zurück zur Website-Eingabe"
                        >
                          <ArrowLeft className="w-5 h-5" />
                        </button>
                      </div>

                      {/* 0. Company Name & Industry (Editable) */}
                      <div className="mb-8 bg-info-soft border border-accent rounded-xl p-6">
                        <h3 className="font-bold text-info mb-4 flex items-center">
                          <Building2 className="w-4 h-4 mr-2" />
                          Firmendaten
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-txt-secondary mb-2">Firmenname</label>
                            <input
                              type="text"
                              value={state.orgName}
                              onChange={(e) => setState(s => ({ ...s, orgName: e.target.value }))}
                              className="w-full px-3 py-2 border border-strong rounded-lg text-sm focus:ring-2 focus:ring-accent/30 focus:border-transparent"
                              placeholder="Ihre Firma GmbH"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-txt-secondary mb-2">
                              Branche {state.industryConfidence >= 80 && '✓'}
                            </label>
                            <select
                              value={state.industry}
                              onChange={(e) => {
                                const newIndustry = e.target.value as any;
                                setState(s => ({ 
                                  ...s, 
                                  industry: newIndustry,
                                  templateId: newIndustry
                                }));
                              }}
                              className="w-full px-3 py-2 border border-strong rounded-lg text-sm focus:ring-2 focus:ring-accent/30 focus:border-transparent"
                            >
                              <option value="automotive">🚗 Automotive</option>
                              <option value="insurance">🛡️ Versicherung</option>
                              <option value="saas">☁️ SaaS</option>
                              <option value="consulting">💼 Consulting</option>
                              <option value="ecommerce">🛒 E-Commerce</option>
                              <option value="agency">🎨 Agentur</option>
                              <option value="finance">🏦 Finanzen</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* 1. AI Generated Images with Prompt Editor */}
                      <div className="mb-8">
                        <h3 className="font-bold text-txt mb-3 flex items-center">
                          <Sparkles className="w-4 h-4 mr-2 text-accent" />
                          KI-Generierte Visionen {state.generatedImages.length > 0 && `(${state.generatedImages.length})`}
                        </h3>
                        
                        {/* Show prompt editor when no images generated yet */}
                        {!state.isGeneratingImages && state.generatedImages.length === 0 && (
                          <div className="bg-gradient-to-br from-accent to-info rounded-xl p-6 border-2 border-subtle mb-4">
                            <h4 className="text-sm font-semibold text-accent mb-2 flex items-center">
                              <Edit3 className="w-4 h-4 mr-2" />
                              Bildgenerierungs-Prompt (bearbeitbar)
                            </h4>
                            <textarea
                              value={state.imagePrompt || buildDefaultImagePrompt(state)}
                              onChange={(e) => setState(s => ({ ...s, imagePrompt: e.target.value }))}
                              className="w-full h-32 p-3 border border-accent rounded-lg text-sm font-mono text-txt-secondary bg-bg-card focus:ring-2 focus:ring-accent/30 focus:border-transparent resize-none"
                              placeholder="Beschreiben Sie die gewünschten Markenbilder..."
                            />
                            <p className="text-xs text-accent mt-2 mb-4">
                              💡 Dieser Prompt wird aus Ihrer gescrapten Marken-DNA generiert. Sie können ihn anpassen, um die Bildgenerierung zu steuern.
                            </p>
                            
                            <button
                              onClick={handleGenerateImages}
                              className="w-full px-6 py-3 bg-gradient-to-r from-accent to-info text-txt-inverted rounded-lg hover:from-accent hover:to-info transition-all shadow-card hover:shadow-card flex items-center justify-center gap-2 font-medium"
                            >
                              <Sparkles className="w-5 h-5" />
                              KI-Bilder mit diesem Prompt generieren
                            </button>
                            <p className="text-xs text-txt-tertiary mt-2 text-center">Optional - Sie können auch ohne KI-Bilder fortfahren</p>
                          </div>
                        )}
                        
                        {/* Show inline loading state */}
                        {state.isGeneratingImages && (
                          <div className="bg-gradient-to-br from-accent to-info rounded-xl p-8 text-center border-2 border-dashed border-accent">
                            <div className="relative w-16 h-16 mx-auto mb-4">
                              <div className="absolute inset-0 border-4 border-accent rounded-full animate-pulse"></div>
                              <div className="absolute inset-0 border-4 border-t-accent rounded-full animate-spin"></div>
                              <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-accent animate-bounce" />
                            </div>
                            <p className="text-accent font-medium mb-1">KI erstellt Ihre Markenbilder...</p>
                            <p className="text-accent text-sm">Dies kann bis zu einer Minute dauern</p>
                          </div>
                        )}
                        
                        {/* Show generated images */}
                        {!state.isGeneratingImages && state.generatedImages.length > 0 && (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                          {state.generatedImages.map((img, index) => (
                            <div 
                              key={index}
                              onClick={() => setState(s => ({ ...s, selectedImageIndex: index }))}
                              className={`group cursor-pointer relative rounded-xl overflow-hidden border-4 transition-all duration-300 ${
                                state.selectedImageIndex === index 
                                  ? 'border-accent shadow-card ring-4 ring-accent/30 transform scale-[1.02]' 
                                  : 'border-transparent hover:border-accent hover:shadow-card'
                              }`}
                            >
                              {/* Image container with fixed aspect ratio */}
                              <div className="aspect-video bg-bg-surface relative">
                                <img 
                                  src={img.url} 
                                  alt={`Style: ${img.style}`} 
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    console.error('Image failed to load:', img.url);
                                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 60"><rect fill="%23e2e8f0" width="100" height="60"/><text x="50" y="35" text-anchor="middle" fill="%2394a3b8" font-size="8">Bild lädt...</text></svg>';
                                  }}
                                />
                              </div>
                              <div className="bg-primary text-primary-contrast p-3 text-center text-sm font-medium">
                                {img.style}
                              </div>
                              {state.selectedImageIndex === index && (
                                <div className="absolute top-3 right-3 bg-accent text-txt-inverted p-1.5 rounded-full shadow-card z-10">
                                  <CheckCircle2 className="w-5 h-5" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                          <button
                              onClick={() => setState(s => ({ ...s, generatedImages: [], selectedImageIndex: -1 }))}
                              className="text-sm text-accent hover:text-accent hover:underline flex items-center gap-1"
                          >
                              <Sparkles className="w-3 h-3" />
                              Mit anderem Prompt neu generieren
                          </button>
                          </>
                        )}
                      </div>

                      {/* 2. Scraped Brand Images */}
                      {state.brandImages.length > 0 && (
                        <div className="mb-8">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-bold text-txt flex items-center">
                              <ImageIcon className="w-4 h-4 mr-2 text-accent" />
                              Gefundene Website-Bilder ({state.brandImages.length})
                            </h3>
                            <div className="text-xs text-txt-tertiary">
                              {state.selectedScrapedImageIndices.length} ausgewählt
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-60 overflow-y-auto p-1">
                            {state.brandImages.map((imgUrl, idx) => {
                              const isSelected = state.selectedScrapedImageIndices.includes(idx);
                              return (
                                <div 
                                  key={idx} 
                                  onClick={() => setState(s => {
                                    const newIndices = isSelected
                                      ? s.selectedScrapedImageIndices.filter(i => i !== idx)
                                      : [...s.selectedScrapedImageIndices, idx];
                                    return { ...s, selectedScrapedImageIndices: newIndices };
                                  })}
                                  className={`aspect-square rounded-lg overflow-hidden border-2 cursor-pointer relative group transition-all ${
                                    isSelected 
                                      ? 'border-accent ring-2 ring-accent/30' 
                                      : 'border-default hover:border-accent'
                                  }`}
                                >
                                  <img src={imgUrl} alt={`Scraped ${idx}`} className="w-full h-full object-cover" />
                                  {isSelected && (
                                    <div className="absolute top-1 right-1 bg-accent text-txt-inverted p-1 rounded-full shadow-card">
                                      <CheckCircle2 className="w-3 h-3" />
                                    </div>
                                  )}
                                  <div className={`absolute inset-0 bg-black/0 transition-colors ${isSelected ? 'bg-info-soft/10' : 'group-hover:bg-black/5'}`} />
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-xs text-txt-tertiary mt-2">Klicken Sie auf die Bilder, die Sie in Ihre Asset-Bibliothek übernehmen möchten.</p>
                        </div>
                      )}

                      {/* 3. Brand Colors Selection */}
                      {state.analysisResult?.analysis.brandColors && state.analysisResult.analysis.brandColors.length > 0 && (
                        <div className="mb-8">
                          <h3 className="font-bold text-txt mb-3 flex items-center">
                            <Palette className="w-4 h-4 mr-2 text-accent" />
                            Markenfarben
                          </h3>
                          <div className="flex flex-wrap gap-3">
                            {state.analysisResult.analysis.brandColors.map((color, idx) => {
                              const isSelected = state.selectedColorIndices.includes(idx);
                              return (
                                <div 
                                  key={idx}
                                  onClick={() => setState(s => {
                                    const newIndices = isSelected
                                      ? s.selectedColorIndices.filter(i => i !== idx)
                                      : [...s.selectedColorIndices, idx];
                                    return { ...s, selectedColorIndices: newIndices };
                                  })}
                                  className={`flex items-center p-2 rounded-lg border-2 cursor-pointer transition-all ${
                                    isSelected 
                                      ? 'border-accent bg-info-soft' 
                                      : 'border-default hover:border-strong'
                                  }`}
                                >
                                  <div 
                                    className="w-6 h-6 rounded-full border border-black/10 mr-2 shadow-card" 
                                    style={{ backgroundColor: color }} 
                                  />
                                  <span className="text-xs font-mono text-txt-secondary mr-2">{color}</span>
                                  {isSelected && <CheckCircle2 className="w-4 h-4 text-accent" />}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* 4. Strategic Content Selection */}
                      <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Offerings */}
                        {state.analysisResult?.analysis.detectedOfferings && state.analysisResult.analysis.detectedOfferings.length > 0 && (
                          <div>
                            <h3 className="font-bold text-txt mb-3 flex items-center">
                              <Package className="w-4 h-4 mr-2 text-warning" />
                              Kernangebote
                            </h3>
                            <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                              {state.analysisResult.analysis.detectedOfferings.map((offering, idx) => {
                                const isSelected = state.selectedOfferings.includes(offering);
                                return (
                                  <label key={idx} className="flex items-center p-2 rounded hover:bg-bg-surface cursor-pointer">
                                    <input 
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => setState(s => {
                                        const newOfferings = isSelected
                                          ? s.selectedOfferings.filter(o => o !== offering)
                                          : [...s.selectedOfferings, offering];
                                        return { ...s, selectedOfferings: newOfferings };
                                      })}
                                      className="w-4 h-4 text-accent rounded border-strong focus:ring-accent/30 mr-3"
                                    />
                                    <span className="text-sm text-txt-secondary">{offering}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Keywords */}
                        {state.analysisResult?.analysis.keywords && state.analysisResult.analysis.keywords.length > 0 && (
                          <div>
                            <h3 className="font-bold text-txt mb-3 flex items-center">
                              <Globe className="w-4 h-4 mr-2 text-success" />
                              SEO Keywords
                            </h3>
                            <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                              {state.analysisResult.analysis.keywords.map((keyword, idx) => {
                                const isSelected = state.selectedKeywords.includes(keyword);
                                return (
                                  <div 
                                    key={idx}
                                    onClick={() => setState(s => {
                                      const newKeywords = isSelected
                                        ? s.selectedKeywords.filter(k => k !== keyword)
                                        : [...s.selectedKeywords, keyword];
                                      return { ...s, selectedKeywords: newKeywords };
                                    })}
                                    className={`px-2 py-1 rounded-full text-xs cursor-pointer border transition-all ${
                                      isSelected 
                                        ? 'bg-success-soft text-success border-success' 
                                        : 'bg-bg-surface text-txt-tertiary border-default hover:border-strong'
                                    }`}
                                  >
                                    {isSelected ? '✓ ' : ''}#{keyword}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 5. Deep Research Results */}
                      {state.deepResearch && (
                        <div className="mb-8">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-bold text-txt flex items-center">
                              <Sparkles className="w-4 h-4 mr-2 text-accent" />
                              AI Deep Research
                            </h3>
                            <div className={`text-xs px-2 py-1 rounded-full ${
                              state.deepResearch.confidence === 'high' ? 'bg-success-soft text-success' :
                              state.deepResearch.confidence === 'medium' ? 'bg-warning-soft text-warning' :
                              'bg-bg-surface text-txt-secondary'
                            }`}>
                              {state.deepResearch.confidence === 'high' ? '✓ Hohe Konfidenz' :
                               state.deepResearch.confidence === 'medium' ? '~ Mittlere Konfidenz' :
                               '! Niedrige Konfidenz'}
                            </div>
                          </div>
                          
                          <div className="bg-bg-card rounded-lg border border-default overflow-hidden">
                            {/* Market Analysis */}
                            <div className="p-4 border-b border-subtle">
                              <h4 className="font-semibold text-txt mb-2 text-sm flex items-center">
                                📊 Marktanalyse
                              </h4>
                              <p className="text-xs text-txt-secondary leading-relaxed whitespace-pre-wrap">{state.deepResearch.marketAnalysis}</p>
                            </div>
                            
                            {/* Competitors */}
                            {state.deepResearch.competitorInsights && state.deepResearch.competitorInsights.length > 0 && (
                              <div className="p-4 border-b border-subtle">
                                <h4 className="font-semibold text-txt mb-3 text-sm flex items-center">
                                  🎯 Wettbewerber ({state.deepResearch.competitorInsights.length})
                                </h4>
                                <div className="space-y-2">
                                  {state.deepResearch.competitorInsights.slice(0, 3).map((comp, i) => (
                                    <div key={i} className="text-xs bg-bg-surface p-3 rounded-lg">
                                      <p className="font-medium text-txt mb-1">{comp.name}</p>
                                      {comp.website && (
                                        <p className="text-txt-tertiary text-[10px] mb-2">{comp.website}</p>
                                      )}
                                      <div className="grid grid-cols-2 gap-2 mt-2">
                                        <div>
                                          <p className="text-[10px] font-semibold text-success mb-1">Stärken:</p>
                                          <ul className="space-y-0.5">
                                            {comp.strengths.slice(0, 2).map((s, idx) => (
                                              <li key={idx} className="text-[10px] text-success">• {s}</li>
                                            ))}
                                          </ul>
                                        </div>
                                        <div>
                                          <p className="text-[10px] font-semibold text-danger mb-1">Schwächen:</p>
                                          <ul className="space-y-0.5">
                                            {comp.weaknesses.slice(0, 2).map((w, idx) => (
                                              <li key={idx} className="text-[10px] text-danger">• {w}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Brand Positioning */}
                            <div className="p-4 border-b border-subtle">
                              <h4 className="font-semibold text-txt mb-2 text-sm flex items-center">
                                🎪 Ihre Positionierung
                              </h4>
                              <p className="text-xs text-txt-secondary leading-relaxed whitespace-pre-wrap">{state.deepResearch.brandPositioning}</p>
                            </div>
                            
                            {/* Strategic Recommendations */}
                            <div className="p-4 bg-accent-soft">
                              <h4 className="font-semibold text-accent mb-2 text-sm flex items-center">
                                💡 Strategische Empfehlungen
                              </h4>
                              <ul className="space-y-1.5">
                                {state.deepResearch.strategicRecommendations.map((rec, i) => (
                                  <li key={i} className="text-xs text-accent flex items-start">
                                    <span className="mr-2 text-accent font-bold">→</span>
                                    <span>{rec}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col md:flex-row justify-end gap-3 pt-4 border-t border-subtle mt-auto">
                        <button
                          onClick={handleGenerateImages} 
                          className="px-6 py-3 border-2 border-default rounded-xl hover:border-strong hover:bg-bg-surface text-txt-secondary font-medium transition-all"
                        >
                          Neu generieren 🔄
                        </button>
                        <button
                          onClick={() => setState(s => ({ ...s, currentStep: 2.9 }))}
                          disabled={state.selectedImageIndex === -1 && state.generatedImages.length > 0}
                          className="px-8 py-3 bg-accent text-txt-inverted rounded-xl hover:bg-accent-bright font-bold shadow-card shadow-glow-accent transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          Weiter zu Sales-Strategie <ArrowRight className="w-5 h-5 ml-2" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
      case 2.9:
        return renderStep2_9();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      case 6:
        return renderStep6();
      default:
        return null;
    }
  };

  // Render with Echobot
  return (
    <>
      {renderWizardContent()}
      
      {/* Echobot - Desktop only, positioned to the right of wizard */}
      <div className="hidden xl:block fixed bottom-6 right-6 z-[60]">
        <Echobot
          initialMessage={echobotMessage}
          initialAnimation={echobotAnimation}
          context="onboarding"
          showOnMount={true}
          enableMouseTracking={false}
        />
      </div>
    </>
  );
};

